import { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { InputNumber, Select, Tooltip, Checkbox } from "antd";
import { IonIcon } from "@ionic/react";
import { resizeOutline } from "ionicons/icons";
import { stringify } from "svgson";
import JSZip from "jszip";
import { downloadBlob } from "../../../utils/nativeDownload";

const SvgExportSettings = forwardRef(function SvgExportSettings({ svgJoined, exportName, onExport }, ref) {
  const [unit, setUnit] = useState("px");
  const [targetWidth, setTargetWidth] = useState(null);
  const [targetHeight, setTargetHeight] = useState(null);
  const [maintainAspectRatio, setMaintainAspectRatio] = useState(true);
  const [originalDimensions, setOriginalDimensions] = useState({ width: 1, height: 1 });
  const [exportLayersSeparately, setExportLayersSeparately] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    if (svgJoined?.attributes?.viewBox) {
      const viewBox = svgJoined.attributes.viewBox.split(" ").map(Number);
      const width = viewBox[2];
      const height = viewBox[3];
      setOriginalDimensions({ width, height });
      setTargetWidth(width);
      setTargetHeight(height);
    }
  }, [svgJoined]);

  const handleWidthChange = (value) => {
    if (!value || value <= 0) return;
    setTargetWidth(value);
    if (maintainAspectRatio) {
      const ratio = originalDimensions.width / originalDimensions.height;
      setTargetHeight(value / ratio);
    }
  };

  const handleHeightChange = (value) => {
    if (!value || value <= 0) return;
    setTargetHeight(value);
    if (maintainAspectRatio) {
      const ratio = originalDimensions.width / originalDimensions.height;
      setTargetWidth(value * ratio);
    }
  };

  const convertUnit = (value, fromUnit, toUnit) => {
    let pixels = value;
    if (fromUnit === "inch") pixels = value * 96;
    else if (fromUnit === "mm") pixels = (value / 25.4) * 96;
    
    if (toUnit === "px") return pixels;
    if (toUnit === "inch") return pixels / 96;
    if (toUnit === "mm") return (pixels / 96) * 25.4;
    return value;
  };

  const getDisplayValue = (pixelValue) => {
    if (!pixelValue) return "";
    const converted = convertUnit(pixelValue, "px", unit);
    if (unit === "px") return Math.round(converted);
    if (unit === "inch") return converted.toFixed(2);
    return converted.toFixed(1);
  };

  const buildDimensionAttrs = () => {
    if (!targetWidth || !targetHeight) return {};
    const widthStr = unit === "px" ? `${Math.round(targetWidth)}` : 
                    unit === "inch" ? `${convertUnit(targetWidth, "px", "inch").toFixed(2)}in` :
                    `${convertUnit(targetWidth, "px", "mm").toFixed(1)}mm`;
    const heightStr = unit === "px" ? `${Math.round(targetHeight)}` : 
                     unit === "inch" ? `${convertUnit(targetHeight, "px", "inch").toFixed(2)}in` :
                     `${convertUnit(targetHeight, "px", "mm").toFixed(1)}mm`;
    return { width: widthStr, height: heightStr };
  };

  const stampSvgTrace = (attrs) => ({
    ...attrs,
    "data-svgtrace": "1",
    "data-svgtrace-version": "1",
  });

  const handleExport = async () => {
    setIsExporting(true);
    try {
    if (exportLayersSeparately && svgJoined.children && svgJoined.children.length > 0) {
      const zip = new JSZip();
      const viewBox = svgJoined.attributes.viewBox;
      const dimAttrs = buildDimensionAttrs();

      for (let i = 0; i < svgJoined.children.length; i++) {
        const layer = svgJoined.children[i];
        const layerSvg = {
          name: "svg",
          type: "element",
          attributes: stampSvgTrace({
            xmlns: "http://www.w3.org/2000/svg",
            viewBox,
            ...dimAttrs,
          }),
          children: [layer],
        };

        const svgString = stringify(layerSvg);
        let layerName = `layer_${i + 1}`;
        const layerFill = layer.attributes?.fill;
        if (layerFill && layerFill.startsWith("#")) {
          layerName = `layer_${i + 1}_${layerFill.replace("#", "")}`;
        }
        zip.file(`${exportName || "export"}_${layerName}.svg`, svgString);
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });
      await downloadBlob(zipBlob, `${exportName || "export"}_layers.zip`);
    } else {
      let svgCopy = JSON.parse(JSON.stringify(svgJoined));
      const dimAttrs = buildDimensionAttrs();
      if (dimAttrs.width) {
        svgCopy.attributes.width = dimAttrs.width;
        svgCopy.attributes.height = dimAttrs.height;
      }
      svgCopy.attributes = stampSvgTrace(svgCopy.attributes);

      const svgString = stringify(svgCopy);
      const blob = new Blob([svgString], { type: "image/svg+xml" });
      await downloadBlob(blob, `${exportName}.svg`);
    }
    
    if (onExport) onExport();
    } catch (error) {
      console.error("SVG export failed:", error);
    } finally {
      setIsExporting(false);
    }
  };

  useImperativeHandle(ref, () => ({
    export: handleExport,
    isExporting,
  }));

  return (
    <div>
      {/* Document Size Card */}
      <div style={{
        background: "#f8f9fa",
        borderRadius: "10px",
        padding: "14px",
        border: "1px solid #e9ecef",
      }}>
        <div style={{ 
          display: "flex", 
          alignItems: "center", 
          marginBottom: "12px",
          justifyContent: "space-between"
        }}>
          <label style={{
            fontSize: "12px",
            fontWeight: 600,
            color: "#888",
            fontFamily: "DM Sans, sans-serif",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}>
            <IonIcon icon={resizeOutline} style={{ fontSize: "15px", color: "#F453C4" }} />
            Document Size
          </label>
          <Select
            value={unit}
            onChange={setUnit}
            size="small"
            style={{ width: 66 }}
            options={[
              { value: "px", label: "px" },
              { value: "inch", label: "in" },
              { value: "mm", label: "mm" },
            ]}
          />
        </div>

        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: "11px", color: "#999", display: "block", marginBottom: "4px" }}>
              Width
            </label>
            <InputNumber
              value={parseFloat(getDisplayValue(targetWidth))}
              onChange={(val) => {
                const pixels = convertUnit(val, unit, "px");
                handleWidthChange(pixels);
              }}
              min={1}
              step={unit === "px" ? 10 : unit === "inch" ? 0.1 : 1}
              style={{ width: "100%" }}
              precision={unit === "px" ? 0 : unit === "inch" ? 2 : 1}
            />
          </div>
          
          <Tooltip title={maintainAspectRatio ? "Unlock aspect ratio" : "Lock aspect ratio"}>
            <button
              onClick={() => setMaintainAspectRatio(!maintainAspectRatio)}
              style={{
                marginTop: "16px",
                padding: "6px 8px",
                border: "1px solid #e9ecef",
                borderRadius: "6px",
                background: maintainAspectRatio ? "#f8f0ff" : "#fff",
                cursor: "pointer",
                fontSize: "14px",
                lineHeight: 1,
                color: maintainAspectRatio ? "#F453C4" : "#bbb",
                transition: "all 0.15s",
                flexShrink: 0,
              }}
            >
              🔗
            </button>
          </Tooltip>

          <div style={{ flex: 1 }}>
            <label style={{ fontSize: "11px", color: "#999", display: "block", marginBottom: "4px" }}>
              Height
            </label>
            <InputNumber
              value={parseFloat(getDisplayValue(targetHeight))}
              onChange={(val) => {
                const pixels = convertUnit(val, unit, "px");
                handleHeightChange(pixels);
              }}
              min={1}
              step={unit === "px" ? 10 : unit === "inch" ? 0.1 : 1}
              style={{ width: "100%" }}
              precision={unit === "px" ? 0 : unit === "inch" ? 2 : 1}
            />
          </div>
        </div>
      </div>

      {/* Export layers separately */}
      <div style={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        marginTop: "12px",
      }}>
        <label style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          fontSize: "13px",
          color: "#555",
          cursor: "pointer",
          padding: "6px 0",
        }}>
          <Checkbox
            checked={exportLayersSeparately}
            onChange={(e) => setExportLayersSeparately(e.target.checked)}
          />
          Export each layer as separate file (ZIP)
        </label>
      </div>

    </div>
  );
});

export default SvgExportSettings;
